"use client";


import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/shared/ui/data-display/avatar";
import { cn } from "@/shared/lib/utils";
import { translateUi } from "@/shared/lib/ui-translate";

export type PodiumMember = {
  userId?: string | null;
  displayName?: string | null;
  avatarUrl?: string | null;
  /**
   * KAN-891: роль в составе. Бэкенд отдаёт её с KAN-806, но тип её не объявлял —
   * и `buildPodiumEntries` молча ронял поле по дороге к витрине. Пьедестал умел
   * рисовать иконку позиции всё это время, ему просто нечего было рисовать.
   */
  role?: string | null;
};

export type PodiumEntry = {
  place: number;
  name: string;
  tag?: string | null;
  score?: string | null;
  /** KAN-891: капитан команды — по нему в составе ставится значок. */
  captainId?: string | null;
  members?: PodiumMember[];
};

// Акценты мест (из макета «Страница турнира.dc.html»): золото / серебро / бронза,
// дальше — нейтрально. accent="" → нейтральная карточка.
const PLACE_ACCENT: Record<number, string> = {
  1: "#f59e0b",
  2: "#94a3b8",
  3: "#ea580c",
};

function themeFor(place: number): { label: string; accent: string } {
  return {
    label:
      place === 1
        ? translateUi("litTournamentPodium.chempion")
        : translateUi("litTournamentPodium.mesto", { place }),
    accent: PLACE_ACCENT[place] ?? "",
  };
}

/**
 * Карточка места на подиуме — компактный горизонтальный вариант из макета:
 * [ранг] [тег/аватар] [имя + подпись места] [счёт], ниже — состав команды.
 * Поддерживает командный (несколько участников) и 1v1 (один участник) режимы.
 */
function PodiumCard({ entry, tie }: { entry: PodiumEntry; tie?: boolean }) {
  const tUi = useTranslations("Ui");
  const { label, accent } = themeFor(entry.place);
  const members = (entry.members ?? []).filter((m) => m.displayName || m.userId);
  const isTeam = members.length > 1;
  const soloMember = !isTeam ? (members[0] ?? null) : null;

  return (
    <div
      className="rounded-[14px] border p-4 shadow-sm"
      style={{
        borderColor: accent
          ? `color-mix(in srgb, ${accent} 45%, var(--border))`
          : "var(--border)",
        background: accent
          ? `color-mix(in srgb, ${accent} 8%, var(--card))`
          : "var(--card)",
      }}
    >
      {/* Шапка карточки */}
      <div className="flex items-center gap-2.5">
        <span
          className="flex h-8 w-8 flex-none items-center justify-center rounded-lg font-heading text-sm font-bold tabular-nums"
          style={
            accent
              ? // Текст на акцентной плашке — цвет фона страницы, а не зашитый тёмный:
                // в светлой теме он читался как чёрный на ярком (KAN-1002).
                { background: accent, color: "var(--background)" }
              : { background: "var(--muted)", color: "var(--foreground)" }
          }
        >
          {entry.place}
        </span>

        {/* Крест: тег команды или аватар игрока (1v1) */}
        {isTeam && entry.tag ? (
          <span
            className="flex-none rounded-md px-2 py-0.5 font-mono text-xs font-semibold"
            style={
              accent
                ? {
                    background: `color-mix(in srgb, ${accent} 18%, transparent)`,
                    color: accent,
                  }
                : { background: "var(--muted)", color: "var(--muted-foreground)" }
            }
          >
            {entry.tag}
          </span>
        ) : soloMember ? (
          <Avatar className="h-7 w-7 flex-none">
            <AvatarImage
              src={soloMember.avatarUrl ?? undefined}
              alt={soloMember.displayName ?? entry.name}
            />
            <AvatarFallback className="text-[10px] font-bold">
              {(soloMember.displayName ?? entry.name).charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
        ) : null}

        <div className="min-w-0 flex-1">
          {soloMember?.userId ? (
            <Link
              href={`/u/${soloMember.userId}`}
              className="block truncate text-sm font-semibold leading-tight transition-colors hover:text-primary"
            >
              {entry.name}
            </Link>
          ) : (
            <div className="truncate text-sm font-semibold leading-tight">
              {entry.name}
            </div>
          )}
          <div
            className="mt-0.5 truncate text-[11px] font-medium"
            style={{ color: accent || "var(--muted-foreground)" }}
          >
            {label}
            {tie ? tUi("litTournamentPodium.nichya") : ""}
          </div>
        </div>

        {entry.score ? (
          <span className="flex-none rounded-md bg-muted px-2 py-0.5 text-xs font-semibold tabular-nums text-foreground/80">
            {entry.score}
          </span>
        ) : null}
      </div>

      {/* Состав команды */}
      {isTeam ? (
        <div className="mt-3 flex flex-col gap-0.5 border-t border-border pt-2.5">
          {members.map((m, i) =>
            m.userId ? (
              <Link
                key={m.userId}
                href={`/u/${m.userId}`}
                className={cn(
                  "flex items-center gap-2 rounded-md px-1.5 py-1 text-sm transition-colors hover:bg-accent",
                )}
              >
                <Avatar className="h-5 w-5 flex-none">
                  <AvatarImage src={m.avatarUrl ?? undefined} alt={m.displayName ?? ""} />
                  <AvatarFallback className="text-[9px]">
                    {(m.displayName ?? "?").charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="truncate">{m.displayName}</span>
              </Link>
            ) : (
              <span
                key={`m-${i}`}
                className="flex items-center gap-2 px-1.5 py-1 text-sm text-muted-foreground"
              >
                <Avatar className="h-5 w-5 flex-none">
                  <AvatarFallback className="text-[9px]">
                    {(m.displayName ?? "?").charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="truncate">{m.displayName}</span>
              </span>
            ),
          )}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Подиум турнира: адаптивная сетка компактных карточек мест (из макета).
 * 1–3 места акцентированы золотом/серебром/бронзой, ничьи помечаются.
 */
export function TournamentPodium({ entries }: { entries: PodiumEntry[] }) {
  const sorted = [...entries].sort((a, b) => a.place - b.place);
  if (sorted.length === 0) return null;

  const placeCounts = sorted.reduce<Record<number, number>>((acc, e) => {
    acc[e.place] = (acc[e.place] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(230px,1fr))]">
      {sorted.map((entry, idx) => (
        <PodiumCard
          key={`${entry.place}-${entry.name}-${idx}`}
          entry={entry}
          tie={(placeCounts[entry.place] ?? 0) > 1}
        />
      ))}
    </div>
  );
}
